import React, { useEffect, useRef, useState } from 'react';

interface IzakayaFinalCtaProps {
  lang: 'ja' | 'en';
}

export const IzakayaFinalCta: React.FC<IzakayaFinalCtaProps> = ({ lang }) => {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const whatsappMsg = encodeURIComponent(
    lang === 'ja'
      ? `こんにちは！東京居酒屋ナイトツアーを予約したいです🍺

【ご希望日】　　年　　月　　日
【参加人数】　　名
【ご希望エリア】荏原町 / 中延 / 戸越 / 武蔵小山（どれでも可）
【ご質問・ご要望】

空き状況と詳細を教えていただけますか？よろしくお願いします！`
      : `Hi! I'd like to book the Tokyo Izakaya Night Tour 🍺

[Preferred Date]: 
[Number of Guests]: 
[Preferred Area]: Ebara-cho / Nakanobu / Togoshi / Musashi-Koyama (any is fine)
[Questions / Requests]: 

Could you share availability and details? Thank you!`
  );
  const whatsappUrl = `https://wa.me/819092895522?text=${whatsappMsg}`;

  const pageUrl = encodeURIComponent(
    lang === 'ja'
      ? 'https://tokyolocalhost.com/tokyo-izakaya-night'
      : 'https://tokyolocalhost.com/tokyo-izakaya-night-en'
  );
  const shareText = encodeURIComponent(
    lang === 'ja'
      ? '東京の昭和路地裏居酒屋ナイトツアー🍺 地元の友人と一緒に本物の東京の夜を体験しよう！'
      : '🍺 Tokyo Local Izakaya Night Tour — Experience authentic Tokyo nightlife like a local!'
  );

  const twitterUrl = `https://twitter.com/intent/tweet?text=${shareText}&url=${pageUrl}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${pageUrl}`;
  const lineUrl = `https://social-plugins.line.me/lineit/share?url=${pageUrl}&text=${shareText}`;
  const instagramUrl = 'https://www.instagram.com/tokyolocalhost/';

  const handleCopyLink = () => {
    const url = lang === 'ja'
      ? 'https://tokyolocalhost.com/tokyo-izakaya-night'
      : 'https://tokyolocalhost.com/tokyo-izakaya-night-en';
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const content = lang === 'ja'
    ? {
        badge: '今夜の東京を体験しよう',
        title: '昭和の路地裏へ、\n一緒に飲みに行こう。',
        subtitle: '観光客が知らない本物の東京の夜。地元の常連が通う居酒屋で、忘れられない一夜を。',
        cta: 'WhatsAppで今すぐ予約',
        sub: '返信率100% ・ 無料相談 ・ 少人数制',
        trust: ['⭐ 5.0 満足度', '🍺 3〜4軒はしご', '👥 最大6名'],
        shareLabel: 'このページをシェアする',
        instagramLabel: 'Instagramをフォロー',
        copyLabel: 'リンクをコピー',
        copiedLabel: 'コピーしました！',
      }
    : {
        badge: 'Experience Tokyo Tonight',
        title: 'Step into the\nReal Tokyo Night.',
        subtitle: 'Hidden izakayas, local regulars, and unforgettable memories — the Tokyo tourists never see.',
        cta: 'Book Now on WhatsApp',
        sub: '100% reply rate · Free consultation · Small groups',
        trust: ['⭐ 5.0 Rating', '🍺 3–4 Bars', '👥 Max 6 People'],
        shareLabel: 'Share this page',
        instagramLabel: 'Follow on Instagram',
        copyLabel: 'Copy Link',
        copiedLabel: 'Copied!',
      };

  return (
    <section id="final-cta" className="relative overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/8e99801c4f58810831fc1b2cf06ff77a.jpg"
          alt="Tokyo izakaya night"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/50"></div>
        <div className="absolute inset-0 bg-amber-900/20"></div>
      </div>

      <div
        ref={ref}
        className={`relative z-10 max-w-4xl mx-auto px-6 py-28 text-center transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/20 rounded-full border border-amber-400/40 mb-6">
          <i className="ri-goblet-line text-amber-400 text-sm"></i>
          <span className="text-amber-300 text-xs font-semibold tracking-widest uppercase">{content.badge}</span>
        </div>

        {/* Title */}
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight whitespace-pre-line">
          {content.title}
        </h2>

        {/* Subtitle */}
        <p className="text-stone-300 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
          {content.subtitle}
        </p>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
          {content.trust.map((item, i) => (
            <div
              key={i}
              className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-sm font-medium"
            >
              {item}
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 px-10 py-5 bg-green-500 hover:bg-green-400 active:scale-95 text-white text-lg font-bold rounded-full shadow-2xl shadow-green-900/50 transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-whatsapp-line text-2xl"></i>
          </div>
          {content.cta}
        </a>

        {/* Sub text */}
        <p className="mt-4 text-stone-400 text-sm">{content.sub}</p>

        {/* Decorative divider */}
        <div className="mt-14 mb-10 flex items-center justify-center gap-4 opacity-40">
          <div className="h-px w-20 bg-amber-400"></div>
          <i className="ri-goblet-fill text-amber-400 text-lg"></i>
          <div className="h-px w-20 bg-amber-400"></div>
        </div>

        {/* SNS Share Section */}
        <div className="flex flex-col items-center gap-5">
          <p className="text-stone-400 text-sm tracking-wider uppercase">{content.shareLabel}</p>

          <div className="flex flex-wrap items-center justify-center gap-3">
            {/* Twitter/X */}
            <a
              href={twitterUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-white/40 rounded-full text-white text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-twitter-x-line text-base"></i>
              </div>
              X (Twitter)
            </a>

            {/* Facebook */}
            <a
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-white/40 rounded-full text-white text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-facebook-circle-line text-base"></i>
              </div>
              Facebook
            </a>

            {/* LINE */}
            <a
              href={lineUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-white/40 rounded-full text-white text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-line-line text-base"></i>
              </div>
              LINE
            </a>

            {/* Copy Link */}
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-white/40 rounded-full text-white text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`text-base ${copied ? 'ri-check-line text-green-400' : 'ri-links-line'}`}></i>
              </div>
              <span className={copied ? 'text-green-400' : ''}>
                {copied ? content.copiedLabel : content.copyLabel}
              </span>
            </button>
          </div>

          {/* Instagram Follow */}
          <a
            href={instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 px-6 py-3 bg-gradient-to-r from-amber-500/20 to-rose-500/20 hover:from-amber-500/30 hover:to-rose-500/30 backdrop-blur-sm border border-amber-400/30 hover:border-amber-400/50 rounded-full text-white text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap mt-1"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-instagram-line text-lg text-amber-300"></i>
            </div>
            <span>{content.instagramLabel}</span>
            <span className="text-amber-300 text-xs">@tokyolocalhost</span>
          </a>
        </div>
      </div>
    </section>
  );
};
